import React from 'react';
import { IconFileText } from '@tabler/icons-react';
import Card from '../Card';
import Text from '../Text';
import './styles.scss';

/**
 * HelpdeskSupport.Card - compact whole-card link for helpdesk/support resources.
 *
 * @param {string} banner Landscape image URL.
 * @param {React.ReactNode} icon Left-side icon, used when banner is not provided.
 * @param {React.ReactNode} title Required title, rendered as Text.h5.
 * @param {React.ReactNode} subtitle Optional subtitle, rendered as Text.span contrast 5.
 * @param {string} url Required destination, opened in a new tab.
 */
function SupportCard({ banner, icon, title, subtitle, url, className = '' }) {
    const hasIconMode = !banner && !!icon;
    const rootClassName = [
        'helpdesk-support-card',
        hasIconMode && 'helpdesk-support-card--icon',
        className,
    ]
        .filter(Boolean)
        .join(' ');

    return (
        <a
            className={rootClassName}
            href={url}
            target="_blank"
            rel="noopener noreferrer"
        >
            <Card flat noBodyPadding className="helpdesk-support-card__surface">
                {banner && (
                    <div className="helpdesk-support-card__banner">
                        <img src={banner} alt="" loading="lazy" />
                    </div>
                )}

                {hasIconMode && (
                    <span className="helpdesk-support-card__icon" aria-hidden="true">
                        {icon}
                    </span>
                )}

                <div className="helpdesk-support-card__content">
                    <div className="helpdesk-support-card__heading">
                        <Text.h5
                            weight={500}
                            contrast={9}
                            className="helpdesk-support-card__title"
                        >
                            {title}
                        </Text.h5>
                    </div>

                    {subtitle && (
                        <Text.span contrast={5} className="helpdesk-support-card__subtitle">
                            {subtitle}
                        </Text.span>
                    )}
                </div>
            </Card>
        </a>
    );
}

/**
 * HelpdeskSupport.CardsGroup - related helpdesk cards rendered in a responsive row.
 *
 * @param {Array<object>} cards Cards passed to HelpdeskSupport.Card.
 * @param {React.ReactNode|false} title Group title. Defaults to "Artigos relacionados".
 */
function CardsGroup({ cards = [], title = 'Artigos relacionados', className = '' }) {
    if (!cards.length) return null;

    const resolvedTitle = title === false ? false : title || 'Artigos relacionados';
    const rootClassName = ['helpdesk-support', className].filter(Boolean).join(' ');

    return (
        <section className={rootClassName} aria-label="Artigos de suporte">
            {resolvedTitle && (
                <Text.h3
                    weight={500}
                    contrast={9}
                    left={<IconFileText />}
                    className="helpdesk-support__title"
                >
                    {resolvedTitle}
                </Text.h3>
            )}

            <div className="helpdesk-support__cards">
                {cards.map((card, index) => (
                    <SupportCard
                        key={card.id || card.url || index}
                        {...card}
                    />
                ))}
            </div>
        </section>
    );
}

const HelpdeskSupport = {
    Card: SupportCard,
    CardsGroup,
};

export default HelpdeskSupport;
